import React, { useState } from 'react';
import { toast } from 'sonner';
import { TabsContent } from '../../../components/ui/tabs';
import { formatCurrency, formatDateDMY } from '../../../lib/utils';
import { ChevronDown, ChevronUp, FileText, Loader2, Trash2, User, Wallet } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../components/ui/alert-dialog';
import api from '../../../lib/api';
import { ContratoFinalizadoCard } from '../../../components/analista/ContratoFinalizadoCard';
export const TabHistoricos = ({ contratos, expandedHistoricos, historicosData, loadingHistoricos, propietarios, setExpandedHistoricos, onHistorialDeleted }) => {
  const [unidadAEliminar, setUnidadAEliminar] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!unidadAEliminar) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/unidades/${unidadAEliminar.unidad_id}/historial`);
      toast.success(`Historial eliminado (${res.data.deleted} registros)`);
      onHistorialDeleted && onHistorialDeleted(unidadAEliminar.unidad_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar el historial');
    } finally {
      setDeleting(false);
      setUnidadAEliminar(null);
    }
  };

  return (
      <>
        <TabsContent value="historicos" className="mt-0">
          <div className="space-y-4">
            {loadingHistoricos ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : historicosData.length === 0 ? (
              <div className="bg-slate-50 rounded-lg p-8 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay registros históricos</p>
                <p className="text-sm text-slate-400 mt-1">Los contratos finalizados, garantías devueltas y cambios de propietario aparecerán aquí.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-500 px-1">
                  {historicosData.length} unidad{historicosData.length !== 1 ? 'es' : ''} con historial
                </p>
                {historicosData.map(item => (
                  <div key={item.unidad_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    {/* Header de unidad - clickeable para expandir */}
                    <div 
                      className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
                      onClick={() => setExpandedHistoricos(prev => ({...prev, [item.unidad_id]: !prev[item.unidad_id]}))}
                    >
                      <div className="flex items-center gap-3">
                        {expandedHistoricos[item.unidad_id] ? (
                          <ChevronUp className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                        <span className="font-semibold text-slate-900">{item.nombre_unidad}</span>
                        <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full">
                          {item.tipo}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        {item.propietarios?.length > 0 && (
                          <span>{item.propietarios.length} propietario{item.propietarios.length !== 1 ? 's' : ''}</span>
                        )}
                        {item.contratos?.length > 0 && (
                          <span>{item.contratos.length} contrato{item.contratos.length !== 1 ? 's' : ''}</span>
                        )}
                        {item.garantias?.length > 0 && (
                          <span>{item.garantias.length} garantía{item.garantias.length !== 1 ? 's' : ''}</span>
                        )}
                        {item.propietarios?.length > 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                            onClick={(e) => { e.stopPropagation(); setUnidadAEliminar(item); }}
                            data-testid={`historial-delete-${item.unidad_id}`}
                            title="Eliminar historial"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Contenido expandido */}
                    {expandedHistoricos[item.unidad_id] && (
                      <div className="p-4 space-y-4">
                        {/* Propietarios anteriores */}
                        {item.propietarios?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                              <User className="w-4 h-4" />
                              Propietarios Anteriores
                            </h4>
                            <div className="space-y-2">
                              {item.propietarios.map((p, idx) => (
                                <div key={idx} className="bg-slate-50 rounded-lg p-3 text-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">{p.propietario_nombre || 'N/A'}</span>
                                    <span className="text-slate-500">
                                      {p.fecha_adquisicion ? formatDateDMY(p.fecha_adquisicion) : '?'} → {p.fecha_fin ? formatDateDMY(p.fecha_fin) : '?'}
                                    </span>
                                  </div>
                                  {p.monto_inversion && (
                                    <p className="text-slate-500 mt-1">
                                      Inversión: {formatCurrency(p.monto_inversion.monto, p.monto_inversion.moneda)}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Contratos finalizados */}
                        {item.contratos?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Contratos Finalizados
                            </h4>
                            <div className="space-y-2">
                              {item.contratos.map((c) => (
                                <ContratoFinalizadoCard key={c.contrato_id} contrato={c} />
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Garantías devueltas */}
                        {item.garantias?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                              <Wallet className="w-4 h-4" />
                              Garantías Devueltas
                            </h4>
                            <div className="space-y-2">
                              {item.garantias.map((g, idx) => (
                                <div key={idx} className="bg-slate-50 rounded-lg p-3 text-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">
                                      {g.tipo_deposito === 'dinero' ? 'Depósito en efectivo' : g.tipo_deposito === 'pagare' ? 'Pagaré' : 'Cheque'}
                                    </span>
                                    <span className="text-green-600 font-medium">
                                      {formatCurrency(g.monto, g.moneda)}
                                    </span>
                                  </div>
                                  <div className="mt-1 text-slate-500 flex items-center gap-4">
                                    <span>Recibido: {g.fecha_cobro ? formatDateDMY(g.fecha_cobro) : '?'}</span>
                                    <span>Devuelto: {g.fecha_devolucion ? formatDateDMY(g.fecha_devolucion) : '?'}</span>
                                    {g.monto_devolucion && (
                                      <span>Monto devuelto: {formatCurrency(g.monto_devolucion, g.moneda)}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Confirm delete historial */}
        <AlertDialog open={!!unidadAEliminar} onOpenChange={(o) => !o && setUnidadAEliminar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar historial de esta unidad?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción eliminará el historial cargado para la unidad <span className="font-semibold">{unidadAEliminar?.nombre_unidad}</span>.
                <br />No se eliminará la unidad ni sus propietarios actuales.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="historial-delete-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="historial-delete-confirm"
              >
                {deleting ? 'Eliminando…' : 'Eliminar historial'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
  );
};
